import { useState } from "react";
import { Link } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { PublicShell, useSeo } from "./glossary/PublicShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  FileText, CheckCircle2, ShieldCheck, HeartHandshake, Gift, Lock,
  Landmark, MapPin, GraduationCap, ClipboardList, Download, BookOpen,
} from "lucide-react";
import { toast } from "sonner";

const INSIDE = [
  { ic: Landmark, t: "Federal money you may be missing", d: "Plain-language walkthroughs of the Disability Tax Credit (DTC), Child Disability Benefit (CDB), RDSP, medical expense and caregiver credits — with a printable federal document checklist." },
  { ic: MapPin, t: "What's available where you live", d: "A province & territory funding overview (OAP/AccessOAP in Ontario, BC, Alberta's FSCD, Saskatchewan, Manitoba and more) plus how to compare programs." },
  { ic: GraduationCap, t: "School supports without the conflict", d: "Accommodations vs. modifications, IEP basics, the exact questions to ask, and a printable School Meeting Prep Sheet." },
  { ic: ClipboardList, t: "An application-readiness checklist", d: "The documents to gather, questions to ask before you apply, common mistakes to avoid, and a simple folder + call-log system." },
  { ic: BookOpen, t: "A step-by-step support plan", d: "A 6-step framework to turn your child's current needs into a prioritized funding map and a clear next action." },
  { ic: ShieldCheck, t: "Trusted sources to confirm details", d: "A curated list of official government sites and community organizations so you always verify before you spend." },
];

const TRUST = [
  { ic: Gift, t: "100% free", d: "No payment, no trial, no credit card. This is a genuine free resource for Canadian families." },
  { ic: HeartHandshake, t: "No obligation", d: "You're never required to sign up for KidPath's paid services. Download it, use it, and that's completely fine with us." },
  { ic: Lock, t: "We respect your inbox", d: "Email is only used to send your guide (and updates if you opt in). Unsubscribe any time — one click." },
];

export default function FundingGuide() {
  useSeo({
    title: "Free Guide: The Canadian Autism Support & Funding Guide — KidPath",
    description: "Download our free 14-page plain-language guide to autism funding in Canada: DTC, CDB, RDSP, provincial programs, school supports (IEPs), and a step-by-step support plan. No cost, no obligation.",
  });

  const [form, setForm] = useState({ name: "", email: "", opt_in: false, company: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [token, setToken] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) { toast.error("Please enter your name."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/guide/request", form);
      setToken(data.download_token);
      setDone(true);
      // trigger the download immediately
      if (data.download_token) window.location.href = `${API}/guide/download/${data.download_token}`;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <PublicShell>
      <section className="max-w-[1180px] mx-auto px-5 md:px-[30px] py-12 sm:py-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          {/* LEFT: pitch */}
          <div>
            <p className="inline-flex items-center gap-2 text-kid-teal font-semibold text-sm mb-3">
              <FileText size={16} /> Free 14-page guide · Made in Canada
            </p>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-kid-navy leading-[1.06]">
              Autism funding in Canada, finally in plain language.
            </h1>
            <p className="text-kid-muted mt-5 text-base sm:text-lg leading-relaxed">
              The system is confusing — federal tax credits, provincial programs, school supports, waitlists, forms.
              This free guide walks you through it step by step, so you know what you may qualify for, what to ask,
              and what to do next. Written for parents and caregivers, not case workers.
            </p>

            <div className="mt-6 flex flex-wrap gap-2.5">
              {["DTC & CDB", "RDSP", "Ontario OAP", "IEPs & school", "Provincial programs", "Application checklist"].map((c) => (
                <span key={c} className="text-sm bg-white border border-kid-line text-kid-navy px-3.5 py-2 rounded-full">{c}</span>
              ))}
            </div>

            {/* Trust row */}
            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              {TRUST.map((t, i) => (
                <div key={i} className="rounded-2xl bg-kid-skytint/50 p-4" data-testid="guide-trust-card">
                  <t.ic size={18} className="text-kid-teal mb-1.5" />
                  <div className="font-semibold text-kid-navy text-sm">{t.t}</div>
                  <p className="text-xs text-kid-muted mt-0.5 leading-relaxed">{t.d}</p>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: form card */}
          <div className="lg:sticky lg:top-24">
            {done ? (
              <div className="rounded-3xl border border-kid-line bg-white p-8 text-center shadow-sm" data-testid="guide-success">
                <div className="w-14 h-14 rounded-2xl bg-kid-teal grid place-items-center mx-auto mb-4"><Download size={26} className="text-white" /></div>
                <h2 className="font-display text-2xl font-extrabold text-kid-navy">Your download is starting…</h2>
                <p className="text-kid-muted mt-2">We've also emailed a copy to <b className="text-kid-navy">{form.email}</b> so you always have it. If the download didn't start, use the button below.</p>
                <a href={token ? `${API}/guide/download/${token}` : "#"} className="inline-flex items-center gap-2 mt-5 bg-kid-teal text-white font-semibold px-6 py-3 rounded-full hover:opacity-90 transition-opacity" data-testid="guide-manual-download">
                  <Download size={18} /> Download the guide (PDF)
                </a>
                <p className="text-xs text-kid-muted mt-5">Remember — this is free with no obligation. When you're ready, you can <Link to="/register" className="text-kid-teal font-semibold hover:underline">create a free KidPath account</Link> for a personalized roadmap.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-3xl border border-kid-line bg-white p-6 sm:p-8 shadow-sm" data-testid="guide-form">
                <div className="flex items-center gap-2 text-kid-navy font-display font-extrabold text-xl mb-1"><Gift size={20} className="text-kid-teal" /> Get the free guide</div>
                <p className="text-sm text-kid-muted mb-5">Tell us where to send it. Takes 10 seconds — no cost, no obligation.</p>

                {/* honeypot */}
                <input type="text" name="company" value={form.company} onChange={(e) => set("company", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-kid-navy">First name</Label>
                    <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jordan" className="mt-1.5 h-11 bg-white" data-testid="guide-name" required />
                  </div>
                  <div>
                    <Label className="text-sm text-kid-navy">Email</Label>
                    <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" className="mt-1.5 h-11 bg-white" data-testid="guide-email" required />
                  </div>
                </div>

                <label className="flex items-start gap-3 mt-5 cursor-pointer" data-testid="guide-optin-label">
                  <Checkbox checked={form.opt_in} onCheckedChange={(v) => set("opt_in", !!v)} className="mt-0.5" data-testid="guide-optin" />
                  <span className="text-sm text-kid-muted leading-relaxed">
                    Yes, send me occasional KidPath tips on funding, school supports and deadlines. <span className="text-kid-navy/70">(Optional — unsubscribe any time.)</span>
                  </span>
                </label>

                <Button type="submit" disabled={submitting} className="w-full mt-6 rounded-full h-12 text-base" data-testid="guide-submit">
                  {submitting ? "Preparing your guide…" : "Send me the free guide"}
                </Button>

                <div className="flex items-center justify-center gap-1.5 text-xs text-kid-muted mt-4">
                  <Lock size={13} /> Your info is safe. We never sell your data.
                </div>
              </form>
            )}
          </div>
        </div>

        {/* WHAT'S INSIDE */}
        <div className="mt-16">
          <div className="max-w-2xl mb-8">
            <p className="inline-flex items-center gap-2 text-kid-teal font-semibold text-sm mb-2"><FileText size={16} /> What's inside</p>
            <h2 className="font-display text-3xl font-extrabold text-kid-navy">14 pages of practical, Canada-specific help</h2>
            <p className="text-kid-muted mt-2">No fluff — just the things families tell us they wish someone had handed them on day one.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {INSIDE.map((s, i) => (
              <div key={i} className="rounded-3xl border border-kid-line bg-white p-6 flex items-start gap-4" data-testid="guide-inside-card">
                <div className="w-11 h-11 rounded-2xl bg-kid-skytint grid place-items-center shrink-0"><s.ic size={20} className="text-kid-teal" /></div>
                <div>
                  <h3 className="font-display text-lg font-bold text-kid-navy">{s.t}</h3>
                  <p className="text-kid-muted mt-1 leading-relaxed text-sm">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* HONEST NOTE */}
        <div className="mt-14 rounded-3xl bg-kid-navy text-white p-8 sm:p-10" data-testid="guide-honest-note">
          <div className="flex items-start gap-3 max-w-3xl">
            <HeartHandshake size={22} className="text-kid-teal shrink-0 mt-1" />
            <div>
              <h3 className="font-display text-xl sm:text-2xl font-extrabold text-white">A quick, honest note</h3>
              <p className="text-white/80 mt-2 leading-relaxed">
                This guide is genuinely free and there's <b className="text-white">no obligation</b> to ever pay for KidPath.
                It offers general information and navigation help — not medical, legal, tax, or financial advice — and you should
                always confirm details with official sources before making decisions. If it helps, that's the whole point.
                And if you'd like a personalized roadmap later, we're here when you're ready.
              </p>
              <div className="mt-5 flex items-center gap-2 text-sm text-white/70">
                <CheckCircle2 size={16} className="text-kid-teal" /> Free forever · <CheckCircle2 size={16} className="text-kid-teal" /> No credit card · <CheckCircle2 size={16} className="text-kid-teal" /> Unsubscribe any time
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
